import moment from "moment-timezone";
import { customAlphabet } from "nanoid";
import toast from "react-hot-toast";
import { ContentStoreDataType } from "../controller/MasterController";
import {
  LANG_CH,
  LANG_DE,
  LANG_EN,
  LANG_ES,
  LANG_FR,
  LANG_JP,
  LANG_KO,
  LANG_RU,
  LANG_TH,
  langText,
} from "../recoil/lang";
import {
  categoryNameLangType,
  conciergeLangType,
  contentLangType,
  CouponType,
  descriptionEventLangType,
  descriptionNotiLangType,
  optionNameLangType,
  optionTypeNameLangType,
  ORDER_CANCLE_STATUS,
  ORDER_COM_COOK_STATUS,
  ORDER_COM_DEL_STATUS,
  ORDER_COM_SERVING_STATUS,
  ORDER_COM_STATUS,
  ORDER_READY_STATUS,
  ORDER_START_DEL_STATUS,
  ORDER_WAIT_STATUS,
  parentStoreSectorNameLangType,
  prdDescriptionLangType,
  prdNameLangType,
  prdTypeNameLangType,
  PRODUCT_TYPE_DELIVERY,
  PRODUCT_TYPE_EXTENSION,
  PRODUCT_TYPE_RENTAL_DIRECT,
  PRODUCT_TYPE_RENTAL_SMS,
  productNameLangType,
  sectorNameLangType,
  storeDescriptionLangType,
  titleLangType,
} from "../recoil/type";
import { Coupons, DeliveryPriceType } from "../controller/StoreController";

declare global {
  interface Window {
    kakao: any;
  }
}

export const getLangCode = (lang: number) => {
  switch (lang) {
    case LANG_KO:
      return "";
    case LANG_EN:
      return "_en";
    case LANG_CH:
      return "_ch";
    case LANG_JP:
      return "_ja";
    case LANG_TH:
      return "_th";
    case LANG_ES:
      return "_es";
    case LANG_FR:
      return "_fr";
    case LANG_RU:
      return "_ru";
    case LANG_DE:
      return "_de";
    default:
      return "";
  }
};

export const contentName = (str: string, lang: number) => {
  if (str === "배달 맛집") {
    return langText.delivery_name[lang];
  } else if (str === "직영 매장") {
    return langText.hotel_store[lang];
  } else if (str === "호텔 소개") {
    return langText.hotel_info[lang];
  } else if (str === "쿠폰") {
    return langText.coupon_name[lang];
  } else if (str === "오늘의 추천") {
    return langText.today_recommendation[lang];
  } else if (str === "커플추천") {
    return langText.couple_recommendation[lang];
  } else if (str === "우리가게 대표") {
    return langText.store_recommendation[lang];
  } else if (str === "호텔 주변 맛집 쿠폰") {
    return langText.restaurants_near_the_hotel[lang];
  } else if (
    str === "가게 사정" ||
    str === "본사 취소" ||
    str === "자동 취소"
  ) {
    return langText.store_situation[lang];
  } else if (str === "고객 취소") {
    return langText.customor_cancel[lang];
  } else if (str === "조리 지연") {
    return langText.cooking_deliay[lang];
  } else if (str === "요청사항 적용 불가") {
    return langText.request_unavailable[lang];
  } else if (str === "배달원 부재") {
    return langText.delivery_Personnel_Absence[lang];
  } else if (str === "재료 소진") {
    return langText.ingredients_exhausted[lang];
  } else if (str === "고객 요청") {
    return langText.customer_request[lang];
  } else if (str === "로비") {
    return langText.lobby[lang];
  } else if (str === "문앞") {
    return langText.at_door[lang];
  } else if (str === "1층 로비") {
    return langText.firstFloorLobby[lang];
  } else if (str === "배달맛집") {
    return langText.store_list[lang];
  } else if (str === "카테고리") {
    return langText.category[lang];
  }
  return str;
};

export const getCategoryName = (
  category: categoryNameLangType & {
    category_name: string;
  },
  lang: number
) => {
  return category[`category_name${getLangCode(lang)}`];
};
export const getPrdName = (
  product: prdNameLangType & {
    prd_name: string;
  },
  lang: number
) => {
  return product[`prd_name${getLangCode(lang)}`];
};
export const getPrdDescription = (
  description: prdDescriptionLangType & {
    prd_description: string;
  },
  lang: number
) => {
  return description[`prd_description${getLangCode(lang)}`];
};
export const getOptionTypeName = (
  option: optionTypeNameLangType & {
    option_type_name: string;
  },
  lang: number
) => {
  return option[`option_type_name${getLangCode(lang)}`];
};
export const getOptionName = (
  option: optionNameLangType & {
    option_name: string;
  },
  lang: number
) => {
  return option[`option_name${getLangCode(lang)}`];
};
export const getPrdTypeName = (
  product: prdTypeNameLangType & {
    prd_type_name: string;
  },
  lang: number
) => {
  return product[`prd_type_name${getLangCode(lang)}`];
};

export const getProductName = (
  product: productNameLangType & {
    product_name: string;
  },
  lang: number
) => {
  return product[`product_name${getLangCode(lang)}`];
};

// 메뉴 카테고리
export const getSectorName = (
  sector: sectorNameLangType & {
    sector_name: string;
  },
  lang: number
) => {
  return sector[`sector_name${getLangCode(lang)}`];
};

export const getStoreEvent = (
  detail: descriptionEventLangType & {
    description_event: string;
  },
  lang: number
) => {
  return detail[`description_event${getLangCode(lang)}`];
};

export const getStoreNoti = (
  detail: descriptionNotiLangType & {
    description_noti: string;
  },
  lang: number
) => {
  return detail[`description_noti${getLangCode(lang)}`];
};

export const getStoreDetail = (
  detail: storeDescriptionLangType & {
    store_description: string;
  },
  lang: number
) => {
  return detail[`store_description${getLangCode(lang)}`];
};

export const getSectorParentName = (
  sector: parentStoreSectorNameLangType & {
    parent_store_sector_name: string;
  },
  lang: number
) => {
  return sector[`parent_store_sector_name${getLangCode(lang)}`];
};

// 매장이름
export const getStoreName = (
  store: {
    store_name: string;
    store_name_en?: string;
  },
  lang: number
) => {
  if (lang === 0) return store.store_name;
  else {
    return store.store_name_en ? store.store_name_en : store.store_name;
  }
};

// 호텔소개
export const getHotelInfoTitle = (
  title: titleLangType & {
    title: string;
  },
  lang: number
) => {
  return title[`title${getLangCode(lang)}`];
};
export const getHotelInfoContent = (
  content: contentLangType & {
    content: string;
  },
  lang: number
) => {
  return content[`content${getLangCode(lang)}`];
};

export const getStoreCouponTitle = (
  title: titleLangType & {
    title: string;
  },
  lang: number
) => {
  return title[`title${getLangCode(lang)}`];
};

// 컨시어지
export const getConciergeName = (
  concierge_name: conciergeLangType & {
    concierge_name: string;
  },
  lang: number
) => {
  return concierge_name[`concierge_name${getLangCode(lang)}`];
};

// 퇴실알림
export const getCheckOutContent = (
  content: contentLangType & {
    content: string;
  },
  lang: number
) => {
  return content[`content${getLangCode(lang)}`];
};

export const generateBillNumber = () => {
  const nanoid = customAlphabet("0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ", 6);
  let sCode = nanoid();

  return sCode;
};

export const moveListItemBack = (arr: any[], index: number) => {
  if (index < 0 || index >= arr.length - 1) {
    console.log("Invalid index.");
    return arr;
  }

  const item = arr.splice(index, 1)[0];
  arr.splice(index + 1, 0, item);

  return arr;
};

export const moveItemForward = (arr: any[], index: number) => {
  if (index <= 0 || index >= arr.length) {
    console.log("Invalid index.");
    return arr;
  }

  const item = arr.splice(index, 1)[0];
  arr.splice(index - 1, 0, item);

  return arr;
};

export const sendInterval = (
  count: number,
  setTimer: (time: number) => void,
  onTimeout: () => void
) => {
  setTimer(count);
  let countValue = count;

  const timer = setInterval(() => {
    if (countValue > 0) {
      countValue = countValue - 1;
      setTimer(countValue);
    } else {
      clearInterval(timer);
      onTimeout();
    }
  }, 1000);

  return timer;
};

export const formatPhoneNumber = (phoneNumber: string) => {
  const cleaned = ("" + phoneNumber).replace(/\D/g, "");
  let formatted = cleaned;
  if (cleaned.length === 9)
    formatted = cleaned.replace(/(\d{2})(\d{3})(\d{4})/, "$1-$2-$3");
  else if (cleaned.length === 10)
    formatted = cleaned.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
  else formatted = cleaned.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
  return formatted;
};

export const formatBusinessNumber = (businessNumber: string) => {
  const cleaned = ("" + businessNumber).replace(/\D/g, "");
  let formatted = cleaned;
  if (cleaned.length === 10)
    formatted = cleaned.replace(/(\d{3})(\d{2})(\d{5})/, "$1-$2-$3");
  return formatted;
};

export const formatCardNumber = (cardNumber: string) => {
  const cleaned = ("" + cardNumber).replace(/\D/g, "");
  let formatted = cleaned;
  if (cleaned.length === 16)
    formatted = cleaned.replace(/(\d{4})(\d{4})(\d{4})(\d{4})/, "$1-$2-$3-$4");
  return formatted;
};

export const formatValidPeriod = (validPeriod: string) => {
  const cleaned = ("" + validPeriod).replace(/\D/g, "");
  let formatted = cleaned;
  if (cleaned.length === 4)
    formatted = cleaned.replace(/(\d{2})(\d{2})/, "$1/$2");
  return formatted;
};

export const formatCardCompany = (company: string) => {
  const cleaned = ("" + company).replace(/\[|\]|카드/g, "");
  return cleaned;
};

export const shufflePassword = () => {
  const array = Array.from({ length: 10 }, (_, index) => index);
  const dividedArray = [];

  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  for (let i = 0; i < 10; i += 3) {
    const dividedSource = array.slice(i, i + 3);
    dividedArray.push(dividedSource);
  }

  return dividedArray;
};

export const getCurrentLocation = (): Promise<{
  latitude: number;
  longitude: number;
}> => {
  return new Promise((resolve, reject) => {
    if ("geolocation" in navigator) {
      /*
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        }
      );
      */
      var ua = navigator.userAgent.toLowerCase(),
        isAndroid = ua.indexOf("android") > -1,
        geoTimeout = isAndroid ? 15000 : 5000;

      navigator.geolocation.getCurrentPosition(
        (position: any) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error: any) => {
          reject(error);
          toast.error("현재 위치를 받기 위해 위치 권한을 허용해주세요.");
        },
        { enableHighAccuracy: true, maximumAge: 3000, timeout: geoTimeout }
      );
    } else {
      toast.error("브라우저에서 위치 정보를 지원하지 않아요.");
      reject(new Error("Geolocation API를 지원하지 않는 브라우저입니다."));
    }
  });
};

export const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return distance;
};

export const getDifferDate = (date: Date, targetDate: Date, lang: number) => {
  const difDay =
    targetDate.getFullYear() * 365 +
    targetDate.getMonth() * 30 +
    targetDate.getDate() -
    (date.getFullYear() * 365 + date.getMonth() * 30 + date.getDate());

  let difDayString = "";
  if (difDay === 2) {
    difDayString = langText.after_tomorrow[lang];
  } else if (difDay === 1) {
    difDayString = langText.tomorrow[lang];
  } else if (difDay > 2) {
    return langText.storeSelector_button_closed[lang];
  }

  const str = `
  ${difDayString !== "" ? `${difDayString} ` : ""}${
    langText.storeSelector_open[lang]
  }\n ${moment(new Date(targetDate)).format("HH:mm")}${
    new Date(targetDate).getHours() >= 12 ? "pm" : "am"
  }
  `;

  return str;
};

export const logToServer = (oData: any) => {
  let oPost = {
    log: JSON.stringify(oData),
  };
  // UtilController.logToServer(oPost)
};

export type OrderStatusType = {
  position: number;
  button_position: number;
  title: string;
  button: string;
};

export const getStatusTypeList = (order_category: number) => {
  if (order_category === PRODUCT_TYPE_DELIVERY) {
    return [
      ORDER_WAIT_STATUS,
      ORDER_READY_STATUS,
      ORDER_COM_COOK_STATUS,
      ORDER_START_DEL_STATUS,
      ORDER_COM_DEL_STATUS,
    ];
  } else if (order_category === PRODUCT_TYPE_EXTENSION) {
    return [ORDER_COM_DEL_STATUS];
  } else if (order_category === PRODUCT_TYPE_RENTAL_DIRECT) {
    return [
      ORDER_WAIT_STATUS,
      ORDER_READY_STATUS,
      ORDER_START_DEL_STATUS,
      ORDER_COM_DEL_STATUS,
    ];
  } else if (order_category === PRODUCT_TYPE_RENTAL_SMS) {
    return [ORDER_WAIT_STATUS, ORDER_READY_STATUS, ORDER_COM_DEL_STATUS];
  } else return [];
};

export const getStoreTypeStatus = (
  order_category: number,
  status: number,
  lang: number
) => {
  if (order_category === PRODUCT_TYPE_DELIVERY) {
    switch (status) {
      case ORDER_WAIT_STATUS:
        return {
          position: 5,
          button_position: 4,
          title: langText.orderStatus_title_checking[lang],
          button: langText.orderStatus_status_confirm[lang],
        };
      case ORDER_READY_STATUS:
        return {
          position: 27.5,
          button_position: 26.5,
          title: langText.orderStatus_title_cooking[lang],
          button: langText.orderStatus_button_cooking[lang],
        };
      case ORDER_COM_COOK_STATUS:
        return {
          position: 50,
          button_position: 49,
          title: langText.orderStatus_title_cooked[lang],
          button: langText.orderStatus_button_cooked[lang],
        };
      case ORDER_START_DEL_STATUS:
        return {
          position: 72.5,
          button_position: 71.5,
          title: langText.orderStatus_title_delivery[lang],
          button: langText.orderStatus_button_delivery[lang],
        };
      case ORDER_COM_SERVING_STATUS:
        return {
          position: 100,
          button_position: 94,
          title: langText.orderStatus_title_complete[lang],
          button: langText.orderStatus_button_complete[lang],
        };
      case ORDER_COM_DEL_STATUS:
        return {
          position: 100,
          button_position: 94,
          title: langText.orderStatus_button_complete[lang],
          button: langText.orderStatus_button_complete[lang],
        };
      case ORDER_COM_STATUS:
        return {
          position: 100,
          button_position: 94,
          title: langText.orderStatus_title_complete[lang],
          button: langText.orderStatus_button_complete[lang],
        };
      case ORDER_CANCLE_STATUS:
        return {
          position: 0,
          button_position: 0,
          title: langText.orderStatus_title_cancel[lang],
          button: langText.orderStatus_button_cancel[lang],
        };
      default:
        return {
          position: 0,
          button_position: 0,
          title: "",
          button: "",
        };
    }
  } else if (order_category === PRODUCT_TYPE_EXTENSION) {
    switch (status) {
      case ORDER_WAIT_STATUS:
        return {
          position: 5,
          button_position: 4,
          title: langText.orderStatus_title_checking[lang],
          button: langText.orderStatus_status_confirm[lang],
        };
      case ORDER_COM_DEL_STATUS:
        return {
          position: 100,
          button_position: 94,
          title: langText.orderStatus_button_complete[lang],
          button: langText.orderStatus_button_complete[lang],
        };
      case ORDER_COM_STATUS:
        return {
          position: 100,
          button_position: 94,
          title: langText.orderStatus_title_complete[lang],
          button: langText.orderStatus_button_complete[lang],
        };
      case ORDER_CANCLE_STATUS:
        return {
          position: 0,
          button_position: 0,
          title: langText.orderStatus_title_cancel[lang],
          button: langText.orderStatus_button_cancel[lang],
        };
      default:
        return {
          position: 0,
          button_position: 0,
          title: "",
          button: "",
        };
    }
  } else if (order_category === PRODUCT_TYPE_RENTAL_DIRECT) {
    switch (status) {
      case ORDER_WAIT_STATUS:
        return {
          position: 5,
          button_position: 4,
          title: langText.orderStatus_title_checking[lang],
          button: langText.orderStatus_status_confirm[lang],
        };
      case ORDER_READY_STATUS:
        return {
          position: 33,
          button_position: 32,
          title: langText.orderStatus_title_preparing_rental[lang],
          button: langText.orderStatus_button_preparing_rental[lang],
        };
      case ORDER_START_DEL_STATUS:
        return {
          position: 63,
          button_position: 62,
          title: langText.orderStatus_title_delivery_rental[lang],
          button: langText.orderStatus_button_delivery_rental[lang],
        };
      case ORDER_COM_DEL_STATUS:
        return {
          position: 100,
          button_position: 94,
          title: langText.orderStatus_title_complete_rental[lang],
          button: langText.orderStatus_button_complete_rental[lang],
        };
      case ORDER_COM_STATUS:
        return {
          position: 100,
          button_position: 94,
          title: langText.orderStatus_title_complete_rental[lang],
          button: langText.orderStatus_button_complete_rental[lang],
        };
      case ORDER_CANCLE_STATUS:
        return {
          position: 0,
          button_position: 0,
          title: langText.orderStatus_title_cancel[lang],
          button: langText.orderStatus_button_cancel[lang],
        };
      default:
        return {
          position: 0,
          button_position: 0,
          title: "",
          button: "",
        };
    }
  } else if (order_category === PRODUCT_TYPE_RENTAL_SMS) {
    switch (status) {
      case ORDER_WAIT_STATUS:
        return {
          position: 5,
          button_position: 4,
          title: langText.orderStatus_title_checking[lang],
          button: langText.orderStatus_status_confirm[lang],
        };
      case ORDER_READY_STATUS:
        return {
          position: 100,
          button_position: 94,
          title: langText.orderStatus_title_complete_sms[lang],
          button: langText.orderStatus_button_complete_sms[lang],
        };
      case ORDER_COM_DEL_STATUS:
        return {
          position: 100,
          button_position: 94,
          title: langText.orderStatus_title_complete_sms[lang],
          button: langText.orderStatus_button_complete_sms[lang],
        };
      case ORDER_COM_STATUS:
        return {
          position: 100,
          button_position: 94,
          title: langText.orderStatus_title_complete_sms[lang],
          button: langText.orderStatus_button_complete_sms[lang],
        };
      case ORDER_CANCLE_STATUS:
        return {
          position: 0,
          button_position: 0,
          title: langText.orderStatus_title_cancel[lang],
          button: langText.orderStatus_button_cancel[lang],
        };
      default:
        return {
          position: 0,
          button_position: 0,
          title: "",
          button: "",
        };
    }
  } else {
    return {
      position: 0,
      button_position: 0,
      title: "",
      button: "",
    };
  }
};

export const getBadgeComment = (store: ContentStoreDataType, lang: number) => {
  const currentTime = new Date();
  if (store.order_count > 30) {
    return langText.badge_popluar[lang];
  } else if (store.rating >= 4.5) {
    return langText.badge_high_rating[lang];
  } else if (
    currentTime.getTime() - new Date(store.ad_started_at).getTime() <
    1000 * 60 * 60 * 24 * 14
  ) {
    return langText.badge_new[lang];
  }
};

export const getAmPm = (time: string) => {
  const splitTime = time.split(":");
  if (Number(splitTime[0]) > 12) return `${splitTime[0]}:${splitTime[1]}pm`;
  else return `${splitTime[0]}:${splitTime[1]}pm`;
};

export const getAvailableCoupons = (
  currentPrice: number,
  couponList: Coupons[]
) => {
  let availableCouponList = [];
  let unavailableCouponList = [];

  for (let i = 0; i < couponList.length; i++) {
    const coupon = couponList[i];
    if (coupon.requirement > currentPrice) {
      unavailableCouponList.push(coupon);
    }
  }

  // *쿠폰 중복 안되는것중 가장 할인 높은것 찾기

  // 중복제한 쿠폰 리스트
  const couponNotDuple = couponList.filter((o) => o.duplicate_user === 0);

  // 가장할인율 높은 중복 제한 쿠폰 (temp)
  let maxCoupon = {
    idx: -1, //쿠폰인덱스
    maxPrice: -1, //할인금액
  };

  // 중복제한중 가장 할인 높은것 찾기
  for (let i = 0; i < couponNotDuple.length; i++) {
    const coupon = couponNotDuple[i];
    if (coupon.requirement > currentPrice) {
      //최소주문금액 만족하지 못하는 쿠폰
      continue;
    }

    //쿠폰(정액)
    if (coupon.coupon_type === 0 && coupon.price > maxCoupon.maxPrice) {
      maxCoupon.idx = i;
      maxCoupon.maxPrice = coupon.price;
      continue;
    } else if (coupon.coupon_type === 1) {
      // 쿠폰(정률)
      let discount = (currentPrice * coupon.percent) / 100;

      //최대할인금액보다 클경우 최대할인금액으로
      if (discount >= coupon.max_discount) {
        discount = coupon.max_discount;
      }

      //최대할인금액보다 클경우 최대할인금액으로
      if (discount > maxCoupon.maxPrice) {
        maxCoupon.idx = i;
        maxCoupon.maxPrice = discount;
      }

      continue;
    }
  }

  if (maxCoupon.idx !== -1) {
    availableCouponList.push(couponNotDuple[maxCoupon.idx]);
  }

  return {
    availableCouponList,
    unavailableCouponList,
  };
};

export const getBestDiscount = (
  currentPrice: number,
  baseDeliveryPrice: number,
  deliveryPrice: DeliveryPriceType[],
  availableCouponList: Coupons[],
  unavailableCouponList: Coupons[]
) => {
  // 현재 할인금액
  const currentDiscount = availableCouponList.reduce((acc: number, cur) => {
    const discount = (currentPrice * cur.percent) / 100;
    const compareValue =
      cur.max_discount === 0
        ? Math.max(cur.price, discount)
        : Math.max(cur.price, Math.min(cur.max_discount, discount));
    return acc + compareValue;
  }, 0);

  // 최종 할인값 결정
  const candidates = unavailableCouponList
    .map((item) => {
      const discount = (item.requirement * item.percent) / 100;
      const compareValue =
        item.max_discount === 0
          ? Math.max(item.price, discount)
          : Math.max(item.price, Math.min(item.max_discount, discount));

      return { ...item, candidate: compareValue };
    })
    .filter((item) => item.candidate > currentDiscount);

  // 가장 작은 필수금액 탐색
  const requirements = candidates.map((item) => item.requirement);
  let minRequired = requirements.length ? Math.min(...requirements) : Infinity;

  // 배열 탐색
  const filtered = candidates.filter(
    (item) => item.requirement === minRequired
  );
  // candidate 중 가장 큰 값
  let maxDiscount = filtered.length
    ? Math.max(...filtered.map((item) => item.candidate))
    : 0;

  console.log("candi", currentDiscount, candidates, minRequired, maxDiscount);

  // 배달 금액과 비교
  let currentDeliveryPrice = baseDeliveryPrice;
  let maxDeliveryPrice = baseDeliveryPrice;
  let deliveryRequired = Infinity;

  // 배달비 리스트 탐색
  deliveryPrice.map((item) => {
    // 현재 가격보다 높은 조건 탐색
    if (currentPrice < item.more_order_price) {
      if (deliveryRequired >= item.more_order_price) {
        deliveryRequired = item.more_order_price;
        maxDeliveryPrice = Math.min(maxDeliveryPrice, item.more_delivery_price);
      }
    } else {
      currentDeliveryPrice = item.more_delivery_price;
    }
  });

  console.log("\n\ncoupon item", currentDiscount, maxDiscount, minRequired);
  console.log(
    "\n\ndelivery item",
    currentDeliveryPrice,
    maxDeliveryPrice,
    deliveryRequired
  );

  if (minRequired === deliveryRequired) {
    maxDiscount =
      maxDiscount - currentDiscount + (currentDeliveryPrice - maxDeliveryPrice);
  } else if (minRequired < deliveryRequired) {
    maxDiscount = maxDiscount - currentDiscount;
  } else {
    minRequired = deliveryRequired;
    maxDiscount = currentDeliveryPrice - maxDeliveryPrice;
  }

  return {
    required: Number.isFinite(minRequired) ? minRequired : 0,
    discount: Math.max(maxDiscount, 0),
  };
};
